import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Flower, MagnifyingGlass, Download, Calendar } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFlowerExchange() {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await axios.get(`${API}/admin/flower-exchanges`, { withCredentials: true });
        setRegistrations(response.data);
        setFilteredRegistrations(response.data);
      } catch (error) {
        console.error('Error fetching registrations:', error);
        toast.error('Failed to load registrations');
      } finally {
        setLoading(false);
      }
    };
    fetchRegistrations();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredRegistrations(registrations);
      return;
    }

    const filtered = registrations.filter(reg => 
      reg.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.state.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredRegistrations(filtered);
  }, [searchTerm, registrations]);

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Address Line 1', 'Address Line 2', 'City', 'State', 'Zip Code', 'Status', 'Registered Date'];
    
    const csvData = filteredRegistrations.map(reg => [
      reg.user_name,
      reg.user_email,
      reg.phone,
      reg.address_line1,
      reg.address_line2 || '',
      reg.city,
      reg.state,
      reg.zip_code,
      reg.status,
      new Date(reg.created_at).toLocaleDateString()
    ]);

    const csv = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flower-exchange-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button 
              data-testid="back-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
            >
              ← Back
            </Button>
            <Flower size={28} weight="fill" className="text-secondary" />
            <span className="text-xl font-heading font-bold">Flower Exchange Admin</span>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="view-users-button"
              onClick={() => navigate('/admin/users')}
              variant="outline"
              className="rounded-full"
            >
              View All Users
            </Button>
            <Button
              data-testid="export-csv-button"
              onClick={exportToCSV}
              variant="outline"
              className="rounded-full"
              disabled={filteredRegistrations.length === 0}
            >
              <Download size={20} className="mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-4xl font-heading font-bold mb-2">Flower Exchange Registrations</h1>
                <p className="text-muted-foreground">
                  Total registrations: <span className="font-bold text-primary">{filteredRegistrations.length}</span>
                  {searchTerm && ` (filtered from ${registrations.length})`}
                </p>
              </div>
            </div>

            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search by name, email, city, or state..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 rounded-xl h-12"
              />
            </div>
          </div>

          {filteredRegistrations.length === 0 ? (
            <div className="text-center py-12 bg-white/80 backdrop-blur-md border border-white/20 rounded-3xl">
              <Calendar size={64} weight="fill" className="text-muted mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">
                {searchTerm ? 'No matching registrations' : 'No registrations yet'}
              </h3>
              <p className="text-muted-foreground">
                {searchTerm ? 'Try adjusting your search terms' : 'Registrations will appear here as members sign up'}
              </p>
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-md border border-white/20 shadow-lg rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">#</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Phone</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Address</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">City</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">State</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Zip</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Registered</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredRegistrations.map((reg, idx) => (
                      <motion.tr
                        key={reg.exchange_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.05 }}
                        className="hover:bg-muted/30 transition-colors"
                        data-testid={`registration-row-${idx}`}
                      >
                        <td className="px-6 py-4 text-sm">{idx + 1}</td>
                        <td className="px-6 py-4">
                          <div className="font-medium">{reg.full_name}</div>
                          <div className="text-xs text-muted-foreground">{reg.user_name}</div>
                        </td>
                        <td className="px-6 py-4 text-sm">{reg.user_email}</td>
                        <td className="px-6 py-4 text-sm">{reg.phone}</td>
                        <td className="px-6 py-4 text-sm max-w-xs">
                          <div>{reg.address_line1}</div>
                          {reg.address_line2 && <div className="text-xs text-muted-foreground">{reg.address_line2}</div>}
                        </td>
                        <td className="px-6 py-4 text-sm">{reg.city}</td>
                        <td className="px-6 py-4 text-sm uppercase">{reg.state}</td>
                        <td className="px-6 py-4 text-sm">{reg.zip_code}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            reg.status === 'pending' ? 'bg-secondary/20 text-secondary' :
                            reg.status === 'confirmed' ? 'bg-accent/20 text-accent' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {reg.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {new Date(reg.created_at).toLocaleDateString()}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-8 bg-gradient-to-br from-[#FF9A9E] to-[#FECFEF] rounded-2xl p-6 text-white">
            <h3 className="font-heading font-semibold mb-2">Quick Tips</h3>
            <ul className="text-white/90 text-sm space-y-1">
              <li>• Use the search bar to quickly find specific registrations</li>
              <li>• Click "Export CSV" to download all data for pairing members</li>
              <li>• Each registration includes agreed status for mailing floral arrangements</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
}